<template>
  <riseup-w-l-progress-loader
    v-if="showContentLoader"
    :job-id="otsJobConfig.jobId"
    :progress-state="progressState"
    @report-issue-click="reportFromLoader"
  />
  <div
    v-else
    class="page"
  >
    <riseup-header :flow-config-id="otsJobConfig.flowConfigId" :force-background-color="'#EEF2F8'" />
    <div
      v-if="showLoader"
      class="loader-wrapper"
    >
      <tiny-loader
        class="loader-class"
        variant="secondary"
      />
    </div>
    <div v-else-if="creds.length > 0 || isPolling" class="else-content">
      <div class="content ready">
        <img
          src="../../../assets/salary-finance/salary-finance-good-to-go.svg"
          class="riseup-wl-connect-ready"
        >
        <div class="center">
          <span class="text">
            Your account is connected so we're ready
            to give you a clear picture of your finances and tips to stay on track
          </span>
        </div>
      </div>
      <div class="footer">
        <button
          class="blue-button font-14"
          @click="handleGoToInsightsClick"
        >
          See your money insights<span class="arrow-right font-18 v-align-middle">→</span>
        </button>
        <button
          class="outline-blue-button font-14"
          @click="handleConnectAccountsClick"
        >
          Connect another account
        </button>

        <p class="complience-text">
          You can also connect more accounts later on
        </p>
        <div class="connection-issue-link">
          <a @click="reportFromConnectAccounts">{{ connectionIssueText }}</a>
        </div>
        <ComplianceText />
      </div>
    </div>
    <div
      v-else
      class="else-content"
    >
      <div class="content">
        <div class="center">
          <img
            src="../../../assets/salary-finance/salary-finance-connect-title.svg"
            class="riseup-wl-connect-title"
          >
          <div
            class="bullets-container"
          >
            <div class="bullet">
              <img
                src="../../../assets/white_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                To get the best results, connect the account you use most for spending.
              </p>
            </div>
            <div class="bullet">
              <img
                src="../../../assets/white_check.svg"
                alt="Checkmark Icon"
              >
              <p class="text">
                Get personalised summaries in minutes.
              </p>
            </div>
          </div>
          <div class="flex-container">
            <div
              class="riseup-wl-flex-box"
            >
              <div class="icon">
                <img
                  src="../../../assets/etoro/etoro-lock.svg"
                  alt="Shield Icon"
                >
              </div>
              <div class="text">
                <span>Your data is secure. We only connect to create your personal insights. </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="footer">
        <button
          class="blue-button"
          @click="handleConnectAccountsClick"
        >
          Connect accounts to get insights<span class="arrow-right font-18 v-align-middle">→</span>
        </button>

        <div class="connection-issue-link">
          <a @click="reportFromConnectAccounts">{{ connectionIssueText }}</a>
        </div>

        <img
          src="../../../assets/salary-finance/salary-finance-partners.svg"
          class="riseup-wl-partners"
        >
        <ComplianceText />
      </div>
    </div>
  </div>
  <error-toast
    v-if="showErrorToast"
    text="Failed to connect your account."
    bold-text="Please try connecting again."
    reason="truelayer_authentication_failed"
    @close="showErrorToast = false"
    @bold-text-click="handleConnectAccountsClick"
  />
  <error-toast
    v-if="showToast"
    :text="toastText"
    color="#012577"
    :reason="toastReason"
    @close="showToast = false"
  />
  <report-popup
    v-if="showReportPopup"
    title="Report a bug"
    subtitle="Please describe in a few words what went wrong:"
    input-placeholder=""
    submit-button-text="Submit"
    cancel-button-text="Cancel"
    @submit="handleReportSubmit"
    @cancel="handleReportCancel"
  />
</template>

<script setup>
import TinyLoader from '@riseupil/base-ui/src/components/TinyLoader/TinyLoader.vue';
import Cookies from 'js-cookie';
import { computed, onBeforeMount, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Segment from '../../../Segment';
import { getCustomerCredentials } from '../../../api/cashflowApi';
import { createPostback } from '../../../api/postbackApi';
import { authenticateTrueLayerCode, getTrueLayerUrl } from '../../../api/trueLayer';
import useContentLoader from '../../../composables/useContentLoader';
import { useProgressTracking } from '../../../composables/useProgressTracking';
import config from '../../../config';
import { useCashflow } from '../../../services/cashflowService';
import { useInsights } from '../../../services/insightsService';
import { useOtsJob } from '../../../services/otsConfigService';
import { useSessionStore } from '../../../stores/session';
import { POLLING_CONFIG } from '../../../types/insight';
import { ProgradPostbackType } from '../../../types/postback';
import { _descopeAutheticate } from '../../../utils/descopeUtils';
import ComplianceText from '../../Common/ComplianceText.vue';
import ErrorToast from '../../Common/ErrorToast.vue';
import RiseupHeader from '../../Common/RiseupHeader.vue';
import RiseupWLProgressLoader from './RiseupWLProgressLoader.vue';
import ReportPopup from './components/ReportPopup.vue';
import DDLogs from '../../../DDLogs';

const sessionStore = useSessionStore();
const { isPolling, updateIsPolling, startPollingByJobId } = useInsights();
const { progressState, updateProgress, reset, setError } = useProgressTracking();
const router = useRouter();
const route = useRoute();
const { toggleLoader, showContentLoader } = useContentLoader();
Segment.identifyCustomer(sessionStore.customerId, sessionStore.memberId);
const { getOtsJobConfig } = useOtsJob();

const otsJobConfig = await getOtsJobConfig();
const { loadCashflowData } = useCashflow();
const creds = ref([]);
const showLoader = ref(false);
const showErrorToast = ref(false);
const showToast = ref(false);
const toastText = ref('');
const toastReason = ref('');
const showReportPopup = ref(false);

const connectionIssueText = computed(() => 'I could not connect my account');

const handleConnectAccountsClick = async () => {
  Segment.trackUserGot('RiseupWLConnectOtsAccountsConnectClicked', { gclid: Cookies.get('gclid') });
  const queryParam = route.query || null;
  window.location.href = await getTrueLayerUrl({
    jobId: otsJobConfig.jobId,
    userId: queryParam?.userId,
    productId: queryParam?.productId,
  }, config.get().otsRiseupWLEnv);
};

const openReportPopup = () => {
  showReportPopup.value = true;
};

const reportFromConnectAccounts = () => {
  toastText.value = 'Thanks for letting us know! Please try again in a few minutes.';
  toastReason.value = `connection_issue_reported_${creds.value.length > 0 ? 'with_accounts' : 'no_accounts'}`;
  openReportPopup();
};

const reportFromLoader = () => {
  toastText.value = "Thanks! We're looking into it.";
  toastReason.value = 'stuck_on_loader';
  openReportPopup();
};

const handleReportSubmit = reportText => {
  showReportPopup.value = false;
  showToast.value = true;
  DDLogs.error('Connection issue reported', {
    report: reportText,
    hasAccounts: creds.value.length > 0,
    reason: toastReason.value,
  });
};

const handleReportCancel = () => {
  showReportPopup.value = false;
  showToast.value = true;
};

const handleGoToInsightsClick = async () => {
  Segment.trackUserGot('RiseupWLConnectOtsAccountsGetInsightsClicked', { gclid: Cookies.get('gclid') });
  reset();
  toggleLoader(true);
  startPollingByJobId(
    otsJobConfig.jobId,
    POLLING_CONFIG.MAX_ATTEMPTS,
    POLLING_CONFIG.POLLING_INTERVAL,
    pollingSuccess,
    handlePollingError,
    updateProgress,
  );
};

const pollingSuccess = async () => {
  Segment.trackUserGot('RiseupWLOtsInsightsReady');

  const queryParam = route.query || null;
  const shouldReload = queryParam?.fromSuccess === 'true';
  await loadCashflowData(6);

  const nextRouteQuery = {};
  if (queryParam?.userId) {
    nextRouteQuery.userId = String(queryParam.userId);
  }
  if (queryParam?.productId) {
    nextRouteQuery.productId = String(queryParam.productId);
  }

  router.push({
    path: '/ots/riseup-wl/success',
    query: nextRouteQuery,
    state: shouldReload ? { shouldReload: true } : undefined,
  });
};

const handlePollingError = () => {
  setError('We encountered an issue processing your data. Please try connecting your accounts again.');
  Segment.trackUserGot('RiseupWLOtsProcessingFailed');
  router.push('/ots/riseup-wl/connect');
};

onBeforeMount(async () => {
  toggleLoader(false);
  showLoader.value = true;
  const queryParam = route.query || null;
  if (queryParam.token) {
    const isDescopeAutheticated = await _descopeAutheticate(queryParam.token);
    if (isDescopeAutheticated) {
      const sessionStore = useSessionStore();
      await sessionStore.init();
    }
  }

  creds.value = await getCustomerCredentials();

  if (queryParam.code) {
    updateIsPolling(true);
    try {
      const response = await authenticateTrueLayerCode(queryParam.code, router);
      if (!response) {
        showErrorToast.value = true;
        updateIsPolling(false);
      }
      if (queryParam.productId && queryParam.userId) {
        await createPostback({
          postbackType: ProgradPostbackType.ConnectAccounts,
          userId: queryParam.userId,
          productId: queryParam.productId,
        }, sessionStore.customerId);
      }
    } catch (error) {
      showErrorToast.value = true;
      updateIsPolling(false);
      console.error('TrueLayer authentication failed:', error);
    }
  }
  showLoader.value = false;

  Segment.trackUserGot('RiseupWLConnectOtsAccountPageEntered');
});

</script>

<style lang="scss" scoped>
@import '../../../scss/riseup-vars';

.page {
  background-color: #EEF2F8;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.content {
  scroll-behavior: smooth;
  width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 40px;
}

.content h1 {
  color: #002579;
  font-size: 28px;
  font-family: GrenetteSemiBold;
  font-weight: 600;
  line-height: 39px;
  margin-bottom: 10px;
}

.content .text {
  color: #002579;
  font-size: 18px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 28px;
  margin-bottom: 20px;
}

.hands {
  width: 200px;
  align-self: center;
  margin-top: 22px;
}

p.below-buttons {
  color: #002579;
  font-size: 16px;
  font-family: CircularStdBook;
  font-style: italic;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
  margin-top: 26px;
}

.flex-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  margin-top: 25px;
}

.riseup-wl-flex-box {
  padding: 15px 15px 15px;
  background: #FFFFFF;
  border-radius: 8px;
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 100%;
}

.riseup-wl-flex-box .text {
  margin: 0 auto;
  text-align: left;
  font-size: 15px;
  line-height: normal;

}

.riseup-wl-flex-box img {
  width: 30px;
  height: 30px;
}

.icon {
  margin-right: 20px;
  display: flex;
  align-items: center;
}

.back-arrow-container {
  padding-left: 21px;
  padding-top: 21px;
  cursor: pointer;
}

.back-arrow {
  width: 24px;
  height: 24px;
}

.arrow-right {
  margin-left: 10px;
  font-family: CircularStdBook;
  color: #FD5F5A;
  font-size: 30px;
  vertical-align: sub;
}

.footer {
  width: 100%;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 40px;
  margin-top: 30px;
}

.riseup-wl-partners {
  margin-top: 20px;
}

.blue-button {
  width: 336px;
  height: 56px;
  border-radius: 12px;
  padding-right: 15px;
  padding-left: 20px;
  gap: 10px;
  background-color: #012577;
  color: #FFFFFF;
  font-size: 16px;
  font-family: CircularStdBook;
  font-weight: 700;
  align-self: center;
}

p.complience-text {
 color: rgba(0, 0, 0, 0.48);
 font-size: 12px;
 font-family: CircularStdBook;
 font-weight: 450;
 line-height: 10px;
 word-wrap: break-word;
 width: 325px;
}

.outline-blue-button {
  width: 336px;
  height: 56px;
  font-size: 16px;
  font-weight: 400;
  background-color: transparent;
  color: #002579;
  border-radius: 12px;
  padding-top: 13px;
  padding-right: 72px;
  padding-bottom: 17px;
  padding-left: 72px;
  gap: 10px;
  margin: 15px;
  border: 1px solid #002579;
}

.bullets-container {
  margin-top: 35px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.bullet {
  display: flex;
  align-items: center;
  gap: 15px;
}

.bullet img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.bullet .text {
  text-align: left;
  font-size: 15px;
  line-height: normal;
  display: flex;
  align-items: flex-start;
  margin: 0;
}

.consent {
  padding-bottom: 10px;
  font-size: 14px;
  font-family: CircularStdBook;
  font-weight: 450;
  line-height: 22px;
  word-wrap: break-word;
  color: #888888;
}

.creds-container {
  display: flex;
  flex-direction: column;
  margin-top: 20px;
}

.riseup-wl-connect-title {
  width: 100%;
  padding: 0px 30px;
  margin-top: 40px;
  margin-bottom: 20px;
}

.riseup-wl-connect-ready {
  width: 100%;
  padding: 0px 60px;
  padding-top: 60px;
}

.cred-box {
  display: flex;
  align-items: center;
  padding: 10px;
  margin: 10px 0;
  background-color: #f0f0f0;
  border-radius: 8px;
  transition: background-color 0.3s ease;
}

.cred-logo {
  width: 30px;
  height: 30px;
  margin-right: 10px;
}

.cred-title {
  font-size: 16px;
  color: #333;
}
.ready {
  gap: 20px;
}

.center {
  text-align: center;
  align-self: center;
  width: 90%;
}

.else-content {
  overflow-y: auto;
  scroll-behavior: smooth;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.separator-line {
  width: 95%;
  height: 0px;
  opacity: 0.20;
  border: 1px black solid;
  margin: 30px auto;
}

.font-14 {
  font-size: 14px;
}

.font-18 {
  font-size: 18px;
}

.v-align-middle {
  vertical-align: middle;
}

.connection-issue-link {
  margin-top: 20px;
  text-align: center;
}

.connection-issue-link a {
  color: #1A1A1A;
  font-family: CircularStdBook;
  font-size: 16px;
  text-decoration: underline;
  text-underline-offset: 4px;
  cursor: pointer;
}
</style>
